import { Injectable } from '@angular/core';
import { HookMatchCriteria, UIRouter } from '@uirouter/angular';
import { BehaviorSubject, Observable } from 'rxjs';
import { STATE_NAMES, uiRouterStateMatches } from 'app/common/state-names';
import { InsightsState } from 'app/common/state-params';
import { createMobileMaxWidthMediaQuery, matchesMobileMaxWidthViewport } from 'app/common/viewport-breakpoints';
import { isInsightsHealthTab } from 'app/services/feature-flags/feature-flag-route-guard.service';
import { StorageService } from 'app/original-services/storage.service';

/**
 * Temporary "for now" restriction: on a mobile-width viewport, only Insights.Health is reachable —
 * every other authenticated state redirects there, unconditionally (whether or not the company
 * actually has Health — `insights.component.ts` shows a "not available" message there instead of
 * the dashboard in that case, rather than this guard picking a different page to land on). The one
 * other exception is the first-company setup page (`settingsCompanies`) when the user has no
 * company yet — there's nothing for Insights to show in that case, so that state is let through too.
 * Kept in its own service (rather than folded into FeatureFlagRouteGuardService, which is about
 * feature flags) so the whole restriction can be deleted in one piece once mobile support widens
 * beyond this single page.
 *
 * Also the single shared source of "is the app on a mobile viewport" for this feature — components
 * that need to hide UI on mobile (navigation, header, insights tabs) read `mobileViewport$`/
 * `isMobileViewport` here instead of each owning their own MediaQueryList and change listener.
 */
@Injectable({ providedIn: 'root' })
export class MobileInsightsOnlyGuardService {
    private readonly mobileMq = createMobileMaxWidthMediaQuery();
    private readonly _mobileViewport$ = new BehaviorSubject<boolean>(matchesMobileMaxWidthViewport(this.mobileMq));
    readonly mobileViewport$: Observable<boolean> = this._mobileViewport$.asObservable();

    get isMobileViewport(): boolean {
        return matchesMobileMaxWidthViewport(this.mobileMq);
    }

    constructor(private router: UIRouter, private storage: StorageService) {
        this.mobileMq?.addEventListener('change', () => {
            this._mobileViewport$.next(this.isMobileViewport);
        });

        const routingService = this.router.stateService;
        const requireAuthCriteria: HookMatchCriteria = {
            to: (state: any) => !!(state?.data && state.data.requireAuth),
        } as any;

        this.router.transitionService.onBefore(requireAuthCriteria, (trans: any) => {
            if (!this.isMobileViewport) {
                return true;
            }
            const toName = trans?.to()?.name;
            const stateParam = trans?.params()?.state;
            if (isInsightsHealthTab(toName, stateParam)) {
                return true;
            }
            if (uiRouterStateMatches(toName, STATE_NAMES.settingsCompanies) && !this.storage.companyId) {
                return true;
            }
            const reload = trans?.options?.()?.reload;
            return routingService.target(
                STATE_NAMES.insightsAIForecastTab,
                { state: InsightsState.AIForecastTab },
                { reload }
            );
        });
    }
}
